import { Directive, OnDestroy, OnInit, Signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { MenuService } from '../../menu.service';
import { WebdokuService } from '../../../web-doku/webdoku.service';
import { BookmarkService } from '../../../bookmarks/bookmark.service';
import { InfoService } from '../../../shared/services/info.service';
import { MenuCommand } from '../../../shared/models/menu-command';
import { delay, map, tap } from 'rxjs/operators';
import { HttpLoadingService } from '../../http-loading.service';
import { RBCheck } from '@ldw/roesberg';
import { SettingsService } from '../../../shared/settings/settings.service';
import { UserInformationService } from '../../user-information.service';
import { SelectionArguments } from '../../../shared/models/selection-arguments';
import { PlanningJob } from '../../../shared/models/planning-job';

@Directive()
export class NavigationBaseComponent implements OnInit, OnDestroy {
  subscription: Subscription = new Subscription();
  _infoText = '';
  loading$ = this.httpLoadingService.loading$.pipe(delay(1));
  selectedPlanningJob: Signal<PlanningJob | undefined>;

  constructor(
    protected menuService: MenuService,
    protected webDokuService: WebdokuService,
    protected bookmarkService: BookmarkService,
    protected infoService: InfoService,
    protected httpLoadingService: HttpLoadingService,
    protected settingsService: SettingsService,
    protected userInformationService: UserInformationService
  ) {
    this.selectedPlanningJob = this.webDokuService.selectedPlanningJob;
  }

  ngOnInit(): void {
    this.subscription.add(
      this.infoService.statusUpdated.subscribe((text) => {
        setTimeout(() => (this._infoText = text));
      })
    );

  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  get isWebDokuAvailable(): boolean {
    return this.webDokuService.isWebDokuSelected;
  }

  get webDokuName(): string {
    return this.webDokuService.currentWebDokuname;
  }

  get infoText(): string {
    return this._infoText;
  }

  get userName(): string {
    const userInfo = this.userInformationService.userInformation;

    if (!userInfo.isEmpty) {
      // return `${userInfo.DisplayName} (${userInfo.LoginName}) - ${userInfo.Department}`;
      return `${userInfo.DisplayName}`;
    }
    return '-';
  }

  get hasWebDokuName(): boolean {
    return !RBCheck.isNullOrEmpty(this.webDokuService.currentWebDokuname);
  }

  get hasInfoText(): boolean {
    return !RBCheck.isNullOrEmpty(this._infoText);
  }

  public get searchButtonVisible(): boolean {
    return this.webDokuService.isWebDokuSelected && this.settingsService.settings.viewMode.searchInHeader;
  }
  protected get allCommands(): MenuCommand[] {
    const arr = [this.menuService.cmdDocuments];

    if (this.webDokuService.currentWebDokuHasLoopNodes) {
      arr.push(this.menuService.cmdLoops);
    }
    if (this.webDokuService.currentWebDokuHasCustomNavigation) {
      arr.push(this.menuService.cmdCustomNavigation);
    }

    if (!this.settingsService.settings.viewMode.searchInHeader) {
      arr.push(this.menuService.cmdSearch);
    }
    return arr;
  }

  public get userCommands(): MenuCommand[] {
    const cmds = [this.menuService.cmdSettings];
    if (this.menuService.cmdDefaultHelp != null) {
      cmds.push(this.menuService.cmdDefaultHelp);
    }
    if (this.menuService.cmdCustomHelp != null) {
      cmds.push(this.menuService.cmdCustomHelp);
    }
    cmds.push(this.menuService.cmdSignOff);
    return cmds;
  }

  public get infoCommands(): MenuCommand[] {
    return [this.menuService.cmdInfoHistory, this.menuService.cmdInfoChanged, this.menuService.cmdInfoWebDoku];
  }

  public get availableItems$(): Observable<MenuCommand[]> {
    const items: MenuCommand[] = [];

    return this.webDokuService.moreThanOneWebDokuAvailable$.pipe(
      tap((moreThanOneWebDokuAvailable) => {
        if (moreThanOneWebDokuAvailable) {
          items.push(this.menuService.cmdWebDoku);
        }
        const tempItems = this.allCommands.filter((item) => {
          return !item.needWebDoku || this.webDokuService.isWebDokuSelected;
        });

        for (const item of tempItems) {
          items.push(item);
        }
      }),
      map(() => items)
    );
  }

  get isMenuVisible(): boolean {
    return !this.settingsService.settings.viewMode.isFullScreen && !this.settingsService.RunningInWindowMode();
  }

  public get planningJobSelectionVisible(): boolean {
    return this.webDokuService.currentPlanningJobs.length > 0;
  }


  jobHasChanged(args: SelectionArguments) {
    this.webDokuService.selectPlanningJob(args.id);
  }
}
